import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";

const STORAGE_KEY = "briology_newsletter_shown_v1";
const CODE = "Briology15";

/**
 * Newsletter pop-up that slides in once the visitor has scrolled ~30% of the
 * page. Uses the same Resend-backed /api/waitlist endpoint for signups so all
 * emails funnel into a single list.
 * Only shown once per browser (persisted in localStorage).
 */
const NewsletterPopup = () => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [joined, setJoined] = useState(false);

  const close = useCallback(() => {
    setOpen(false);
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    let alreadyShown = false;
    try {
      alreadyShown = localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      /* ignore */
    }
    if (alreadyShown) return;

    const onScroll = () => {
      const scrolled = window.scrollY;
      const max =
        document.documentElement.scrollHeight - window.innerHeight;
      if (max <= 0) return;
      const pct = scrolled / max;
      if (pct >= 0.3) {
        setOpen(true);
        window.removeEventListener("scroll", onScroll);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Keyboard: Escape closes
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email.trim() || !email.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    try {
      setSubmitting(true);
      await api.post("/waitlist", {
        email: email.trim(),
        source: "newsletter-popup",
      });
      setJoined(true);
      toast.success("You're in — check your inbox 💛");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="newsletter-popup"
      className="fixed inset-0 z-[1000] flex items-center justify-center px-4 py-6"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <button
        aria-label="Close newsletter"
        onClick={close}
        className="absolute inset-0 bg-black/45 backdrop-blur-[2px] cursor-default"
      />

      {/* Popup card */}
      <div
        data-testid="newsletter-card"
        className="relative w-full max-w-3xl bg-white border border-black grid md:grid-cols-2 items-stretch overflow-hidden"
        style={{
          boxShadow:
            "0 30px 60px -20px rgba(60,40,20,0.45), 0 12px 24px -10px rgba(60,40,20,0.25)",
        }}
      >
        {/* Close button */}
        <button
          data-testid="newsletter-close"
          onClick={close}
          aria-label="Close"
          className="absolute top-2 right-2 z-10 w-9 h-9 flex items-center justify-center bg-white border border-black text-black hover:bg-black hover:text-white transition-colors font-serif-display text-lg leading-none"
        >
          ×
        </button>

        {/* Left — hero photo (real <img> so it truly covers with no gaps) */}
        <div className="relative w-full h-full min-h-[280px] md:min-h-0 overflow-hidden bg-[#F4E5C9]">
          <img
            src="/assets/briology_hero.png"
            alt="Briology · custom perfume oil"
            draggable={false}
            className="absolute inset-0 w-full h-full block"
            style={{ objectFit: "cover", objectPosition: "55% 60%" }}
          />
        </div>

        {/* Right — copy + form */}
        <div className="p-6 md:p-8 flex flex-col justify-center">
          <div className="overline text-[10px] tracking-[0.3em] text-[#FF1493] mb-3">
            Members only
          </div>
          <h2 className="font-serif-display font-black text-3xl md:text-[38px] leading-[1.05] tracking-tight mb-3">
            15% off your
            <br />
            first order.
          </h2>
          <p className="text-sm text-black/70 leading-[1.55] mb-5">
            Sign up for the Briology newsletter and unlock your exclusive
            welcome code.
          </p>

          {joined ? (
            <div
              data-testid="newsletter-success"
              className="border border-black bg-[#FFF1F5] p-4"
            >
              <div className="overline text-[10px] tracking-[0.3em] text-[#FF1493] mb-1">
                Your code
              </div>
              <div className="font-serif-display font-black text-2xl md:text-3xl tracking-tight text-black select-all">
                {CODE}
              </div>
              <p className="mt-2 text-xs text-black/60 italic">
                Use at checkout. We&apos;ve also sent it to your inbox.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="flex flex-col gap-2">
              <label
                htmlFor="newsletter-email"
                className="overline text-[9.5px] tracking-[0.25em] text-black/70"
              >
                Your email
              </label>
              <div className="flex border border-black">
                <input
                  id="newsletter-email"
                  data-testid="newsletter-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@briology.com"
                  className="flex-1 px-3 py-3 bg-transparent outline-none font-sans-display text-sm placeholder:text-black/40"
                  required
                />
                <button
                  type="submit"
                  data-testid="newsletter-submit"
                  disabled={submitting}
                  className="bg-black text-white overline text-[10.5px] tracking-[0.22em] px-4 border-l border-black hover:bg-[#FF1493] disabled:opacity-60 transition-colors"
                >
                  {submitting ? "…" : "Get code"}
                </button>
              </div>
              <p className="text-[10.5px] text-black/50 italic mt-1">
                One welcome email + occasional love letters. Unsubscribe anytime.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewsletterPopup;
